/**
 * SnapshotCard and SnapshotCardRow
 *
 * Unified metric/snapshot cards for dashboard rows (Spaces, Nexus, CRM).
 *
 * Primary responsibilities:
 * - Single card primitive with title, value, subtitle, optional header right (icon/chevron)
 * - Variant-based styling: neutral or pastel (green, blue, purple, orange)
 * - Row container for consistent grid layout
 */

'use client'

import * as React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export type SnapshotCardVariant = 'neutral' | 'green' | 'blue' | 'purple' | 'orange'

const variantStyles: Record<
  SnapshotCardVariant,
  { card: string; title: string; value: string; subtitle: string; headerRight: string }
> = {
  neutral: {
    card: 'border-0 bg-muted/20 shadow-none',
    title: 'text-muted-foreground',
    value: 'text-foreground',
    subtitle: 'text-muted-foreground',
    headerRight: 'text-muted-foreground'
  },
  green: {
    card:
      'border-green-200 bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-950 dark:to-emerald-900',
    title: 'text-green-800 dark:text-green-200',
    value: 'text-green-700 dark:text-green-300',
    subtitle: 'text-green-600 dark:text-green-400',
    headerRight: 'text-green-600 dark:text-green-400'
  },
  blue: {
    card:
      'border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900',
    title: 'text-blue-800 dark:text-blue-200',
    value: 'text-blue-700 dark:text-blue-300',
    subtitle: 'text-blue-600 dark:text-blue-400',
    headerRight: 'text-blue-600 dark:text-blue-400'
  },
  purple: {
    card:
      'border-purple-200 bg-gradient-to-br from-purple-50 to-violet-100 dark:from-purple-950 dark:to-violet-900',
    title: 'text-purple-800 dark:text-purple-200',
    value: 'text-purple-700 dark:text-purple-300',
    subtitle: 'text-purple-600 dark:text-purple-400',
    headerRight: 'text-purple-600 dark:text-purple-400'
  },
  orange: {
    card:
      'border-orange-200 bg-gradient-to-br from-orange-50 to-amber-100 dark:from-orange-950 dark:to-amber-900',
    title: 'text-orange-800 dark:text-orange-200',
    value: 'text-orange-700 dark:text-orange-300',
    subtitle: 'text-orange-600 dark:text-orange-400',
    headerRight: 'text-orange-600 dark:text-orange-400'
  }
}

/** Props for a single metric/snapshot card. Use with SnapshotCardRow for dashboard rows. */
export interface SnapshotCardProps {
  title: string
  value: React.ReactNode
  subtitle?: React.ReactNode
  /** Icon or chevron shown in the header (right). */
  headerRight?: React.ReactNode
  variant?: SnapshotCardVariant
  density?: 'default' | 'compact'
  trend?: { value: string; isPositive: boolean }
  badge?: React.ReactNode
  className?: string
  children?: React.ReactNode
  ariaLabel?: string
  /** ID for the subtitle element; Card uses aria-describedby when set. */
  ariaDescriptionId?: string
}

export const SnapshotCard = React.forwardRef<HTMLDivElement, SnapshotCardProps>(
  function SnapshotCard(
    {
      title,
      value,
      subtitle,
      headerRight,
      variant = 'neutral',
      density = 'default',
      trend,
      badge,
      className,
      children,
      ariaLabel,
      ariaDescriptionId
    },
    ref
  ) {
  const styles = variantStyles[variant]
  const isCompact = density === 'compact'
  return (
    <Card
      ref={ref}
      className={cn(styles.card, className)}
      aria-label={ariaLabel}
      aria-describedby={ariaDescriptionId ?? undefined}
    >
      <CardHeader
        className={cn(
          'flex flex-row items-center justify-between space-y-0',
          isCompact ? 'p-3 pb-1.5' : 'p-4 pb-2'
        )}
      >
        <CardTitle
          className={cn(
            isCompact ? 'text-xs font-medium' : 'text-sm font-medium',
            styles.title
          )}
        >
          {title}
        </CardTitle>
        {headerRight != null && (
          <div className={cn('shrink-0', styles.headerRight)}>{headerRight}</div>
        )}
      </CardHeader>
      <CardContent className={cn('pt-0', isCompact ? 'p-3' : 'p-4')}>
        <div className="flex flex-wrap items-baseline gap-2">
          <div
            className={cn(
              isCompact ? 'text-xl' : 'text-2xl',
              'font-bold tabular-nums',
              styles.value
            )}
          >
            {value}
          </div>
          {badge != null && <span className="shrink-0">{badge}</span>}
          {trend != null && (
            <span
              className={cn(
                'text-xs',
                trend.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}
            >
              {trend.isPositive ? '+' : ''}
              {trend.value}
            </span>
          )}
        </div>
        {subtitle != null && (
          <p
            id={ariaDescriptionId}
            className={cn('mt-1 text-xs', styles.subtitle)}
          >
            {subtitle}
          </p>
        )}
        {children}
      </CardContent>
    </Card>
  )
})
SnapshotCard.displayName = 'SnapshotCard'

export type SnapshotCardRowColumns = 2 | 3 | 4
export type SnapshotCardRowGap = 'sm' | 'md' | 'lg'

/** Props for a row of snapshot cards. Grid is 1 column on xs, then columns at sm/lg. */
export interface SnapshotCardRowProps {
  columns?: SnapshotCardRowColumns
  gap?: SnapshotCardRowGap
  className?: string
  children: React.ReactNode
}

const gapMap = { sm: 'gap-3', md: 'gap-4', lg: 'gap-6' }
const columnsMap = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4'
}

export function SnapshotCardRow({
  columns = 4,
  gap = 'md',
  className,
  children
}: SnapshotCardRowProps) {
  return (
    <div
      className={cn(
        'grid grid-cols-1',
        gapMap[gap],
        columnsMap[columns],
        className
      )}
    >
      {children}
    </div>
  )
}
